// RITA-1547 — Aus einer Vorlage wird ein Entwurf: Die Platzhalter werden zu normalem Text mit den Daten des
// Beraters, ihre Formatierung (fett, kursiv, unterstrichen, Schriftgröße) bleibt (Julia 20.10.2026,
// Entscheidung 2). Nur das heutige Datum bleibt ein Platzhalter und zeigt immer den heutigen Tag, später
// also den Drucktag (Bildentwurf 3b, F4). Leere Werte entfallen wie in der Vorschau.
//
// Zum Erkennen späterer Änderungen merkt sich der Entwurf die eingesetzten Werte der verwendeten
// Platzhalter (`advisor_values`). Der Name der Bearbeiterin und das Datum hängen nicht vom Berater ab
// und zählen deshalb nicht als „Berater-Daten“ (F5: nur Felder, die in den Dokumenten vorkommen).
import { normalisiereDokument } from '../../templates/contractEditor/model/bearbeiten';
import { text, zeilenumbruch } from '../../templates/contractEditor/model/erzeugen';
import type { Block, Inlineknoten, Vertragsdokument } from '../../templates/contractEditor/model/types';
import { fehlendeVertragspartnerAngaben, verwendetePlatzhalter } from './fehlendeAngaben';

type Werte = Readonly<Partial<Record<string, string>>>;

/** Bleibt im Entwurf ein Platzhalter (F4); der Server lässt nur diesen zu. */
export const BEWEGLICHE_PLATZHALTER: readonly string[] = ['HEUTIGES_DATUM'];

/** Werte, die nicht von den Daten des Beraters abhängen. */
const KEINE_BERATERDATEN: readonly string[] = ['HEUTIGES_DATUM', 'BEARBEITER'];

function inhaltAlsText(inhalt: Inlineknoten[], werte: Werte): Inlineknoten[] {
  return inhalt.flatMap((knoten): Inlineknoten[] => {
    if (knoten.typ !== 'platzhalter' || BEWEGLICHE_PLATZHALTER.includes(knoten.schluessel)) return [knoten];
    const { typ, schluessel, ...format } = knoten;
    const wert = werte[schluessel] ?? '';
    // Ein Zeilenumbruch im Wert wird wie im Editor ein eigener Knoten.
    return wert
      .split('\n')
      .flatMap((zeile, index): Inlineknoten[] => [
        ...(index > 0 ? [zeilenumbruch()] : []),
        ...(zeile ? [text(zeile, format)] : []),
      ]);
  });
}

function bloeckeAlsText<T extends Block>(bloecke: T[], werte: Werte): T[] {
  return bloecke.map((block) => {
    if (block.typ === 'absatz') return { ...block, inhalt: inhaltAlsText(block.inhalt, werte) };
    if (block.typ === 'liste') return { ...block, eintraege: block.eintraege.map((e) => inhaltAlsText(e, werte)) };
    return block;
  });
}

/** Das Dokument einer Vorlage mit eingesetzten Werten; benachbarte gleich formatierte Texte verschmelzen. */
export function platzhalterAlsText(dokument: Vertragsdokument, werte: Werte): Vertragsdokument {
  return normalisiereDokument({
    ...dokument,
    kopf: { ...dokument.kopf, inhalt: bloeckeAlsText(dokument.kopf.inhalt, werte) },
    text: bloeckeAlsText(dokument.text, werte),
    fuss: { ...dokument.fuss, inhalt: bloeckeAlsText(dokument.fuss.inhalt, werte) },
  });
}

/** Die eingesetzten Berater-Daten der in den Vorlagen verwendeten Platzhalter (für `advisor_values`). */
export function eingesetzteBeraterwerte(vorlagen: Vertragsdokument[], werte: Werte): Record<string, string> {
  const ergebnis: Record<string, string> = {};
  vorlagen.forEach((dokument) =>
    verwendetePlatzhalter(dokument).forEach((schluessel) => {
      if (!KEINE_BERATERDATEN.includes(schluessel)) ergebnis[schluessel] = werte[schluessel] ?? '';
      if (schluessel === 'VERTRAGSPARTNER') {
        fehlendeVertragspartnerAngaben(werte).forEach((angabe) => {
          ergebnis[angabe] = werte[angabe] ?? '';
        });
      }
    }),
  );
  return ergebnis;
}

/**
 * RITA-1600 (Fund 107): Schlüssel der Berater-Daten, die ein Dokument braucht – dieselben, die
 * `eingesetzteBeraterwerte` für es merken kann ({{VERTRAGSPARTNER}} auch mit Anrede, Vor- und Nachname). Damit nennt
 * die Rückfrage „Beim Anlegen fehlten Angaben“ nur, was die angehakten Dokumente brauchen.
 */
export function beraterschluessel(dokument: Vertragsdokument): string[] {
  const schluessel = verwendetePlatzhalter(dokument).filter((eintrag) => !KEINE_BERATERDATEN.includes(eintrag));
  if (schluessel.includes('VERTRAGSPARTNER')) schluessel.push('ANREDE', 'VORNAME', 'NACHNAME');
  return Array.from(new Set(schluessel));
}

/**
 * Schlüssel der Berater-Daten, die sich seit dem Beginnen geändert haben (Reihenfolge wie gespeichert).
 * Verglichen werden nur die gemerkten Platzhalter, also nur Angaben, die in den Dokumenten vorkommen.
 */
export function geaenderteBeraterwerte(gemerkt: Readonly<Record<string, string>> | null, werte: Werte): string[] {
  if (!gemerkt) return [];
  return Object.keys(gemerkt).filter((schluessel) => (werte[schluessel] ?? '') !== gemerkt[schluessel]);
}
